import { FC, useRef, useState } from 'react'

import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import ShrinkIcon from '@/components/icons/pkg-shrink.svg?react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
interface ShrinkConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (notes: string) => void
  isLoading?: boolean
}

export const ShrinkConfirmationDialog: FC<ShrinkConfirmationDialogProps> = ({ isOpen, onClose, onConfirm, isLoading = false }) => {
  const [notes, setNotes] = useState('')
  const [showInvalidCharTooltip, setShowInvalidCharTooltip] = useState(false)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Check if character is valid (English letters, numbers, and common symbols)
  const isValidChar = (char: string): boolean => {
    // Allow ASCII characters from 32 (space) to 126 (~)
    // This includes: letters (A-Z, a-z), numbers (0-9), and common symbols
    const charCode = char.charCodeAt(0)
    return charCode >= 32 && charCode <= 126
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value

    // Filter out invalid characters
    const filteredValue = inputValue
      .split('')
      .filter(char => isValidChar(char))
      .join('')

    // Check if any characters were filtered out (invalid characters detected)
    if (filteredValue.length < inputValue.length) {
      // Show tooltip
      setShowInvalidCharTooltip(true)

      // Clear previous timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }

      // Hide tooltip after 3 seconds
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowInvalidCharTooltip(false)
      }, 3000)
    } else {
      // Hide tooltip if no invalid characters
      setShowInvalidCharTooltip(false)
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }

    setNotes(filteredValue)
  }

  const handleSubmit = async () => {
    onConfirm(notes)
  }

  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={onClose}
      title={
        <div className='space-y-4 text-center'>
          <div className='mx-auto h-6 w-6'>
            <ShrinkIcon className='text-icon-primary h-full w-full' />
          </div>
          <div className='text-lg font-medium'>רוצה לצמצם את נפח האריזה שלך?</div>
        </div>
      }
      dialogContentClassName='max-w-lg !p-0'
    >
      <div className='flex flex-col items-center gap-6 px-6 pt-2 text-center'>
        <p className='text-text-secondary text-sm leading-relaxed'>
          אנחנו דואגים לחבילה שלך!
          <br />
          ברוב המקרים אנחנו מסירים אריזות אוטומטית,
          <br />
          כאן השארנו אותן כי המוצר אינו טקסטיל וייתכן שהוא רגיש.
          <br />
          נוכל לצמצם בכל זאת – אך שים/י לב:
          <br />
          אין אחריות על נזק למוצרים לאחר הסרת אריזות,
          <br />
          במיוחד אם הם שבירים
        </p>

        <TooltipProvider>
          <Tooltip open={showInvalidCharTooltip}>
            <TooltipTrigger asChild>
              <div className='w-full'>
                <Textarea
                  id='warehouse-notes'
                  label='הערות למחסן (אופציונלי)'
                  placeholder='הזן הערות למחסן...'
                  value={notes}
                  onChange={handleInputChange}
                  maxLength={500}
                  className='w-full'
                  supportingText='אנא כתבו את ההערות באנגלית בלבד. ניתן להזין עד 500 תווים'
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side='top' dir='rtl'>
              ניתן להזין רק תווים באנגלית
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-3 py-2'>
          <p className='text-text-secondary text-xs'>השירות כרוך בתשלום של $5 שיופיע בחשבון לאחר הביצוע</p>
        </div>
      </div>
      <Divider className='my-6' />
      <div className='flex justify-center px-6 pb-6'>
        <Button loading={isLoading} onClick={handleSubmit} disabled={isLoading} className='w-full max-w-[335px]' size='lg'>
          בואו נצמצם
        </Button>
      </div>
    </AppDialog>
  )
}
